<script setup>
const fullWidthMode = inject("fullWidthMode", false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));
import YongsinFiveChart from './YongsinFiveChart.vue';
import YongsinDonutChart from './YongsinDonutChart.vue';
import { useDeviceDetect } from '@/composables/useDeviceDetect';

const { isMobile } = useDeviceDetect();

import {
  parseKeywords,
  insertLineBreak,
  transformMarkedText,
  splitBySentence,
} from '@/composables/splitSentence';
const props = defineProps({
  yData: Object,
});

// 용/희/기/구/한 차트 중간 설명
const hanchartDesc = computed(() => {
  const sentences = splitBySentence(props.yData?.분석상세?.용희기구한분석?.오행분석 || '');
  return sentences.map((sentence) => transformMarkedText(sentence, { keywordClass: 'y-keyword' }));
});

// 용/희/기/구/한 차트
const hanDefaultData = computed(() => props.yData?.분석상세?.용희기구한분석?.기본비율); // 기본비율
const hanjijangData = computed(() => props.yData?.분석상세?.용희기구한분석?.지장비율); // 지장비율
const hanChartColor = computed(() => props.yData?.분석상세?.용희기구한분석?.오행분류); // 차트컬러
const fiveMap = {
  화: { hanja: '火', class: 'five01' },
  수: { hanja: '水', class: 'five02' },
  목: { hanja: '木', class: 'five03' },
  금: { hanja: '金', class: 'five04' },
  토: { hanja: '土', class: 'five05' },
  '-': { hanja: '-', class: 'none' },
};

// 선정용신
const selectedType = computed(() => props.yData?.분석상세?.선정용신?.분석유형);
const selectedYong = computed(() => props.yData?.분석상세?.선정용신?.분석용신);
const yongLabel = computed(() => fiveMap[selectedYong.value]?.hanja || '');
const yongClass = computed(() => fiveMap[selectedYong.value]?.class || '');
const yongPosibility = computed(() => props.yData?.분석상세?.선정용신?.용신확률);
const confidenceScore = computed(() => props.yData?.분석상세?.선정용신?.예측통계?.['신뢰 구간']);
const confideceLevel = computed(() => props.yData?.분석상세?.선정용신?.분석신뢰도);
const ggPattern = computed(() => props.yData?.분석상세?.용희기구한분석?.격국패턴);
const candidateOrder = ['억부용신', '병약용신', '통관용신', '조후용신', '격국용신'];
const yongsinCandidates = computed(() => {
  const source = props.yData?.분석상세?.용신후보 || {};
  return candidateOrder.map((key) => {
    const value = source[key] || '';
    const elements = value ? value.split('') : null;
    return {
      label: key,
      elements: elements?.map((el) => fiveMap[el]) ?? null,
    };
  });
});
const analysisSteps = computed(() => props.yData?.분석상세?.선정용신?.분석설명);
const formattedAnalysisSteps = computed(() => {
  return (analysisSteps.value || '')
    .split('|')
    .map((str) => str.trim())
    .filter(Boolean)
    .map((sentence) =>
      transformMarkedText(sentence, { keywordClass: `yongsin__highlight-text ${yongClass.value}` }),
    );
});
</script>

<template>
  <VRow class="full-height">
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-first">
      <VCard class="personality-card full-height right">
        <div class="detail-card__top-title yongsin content_chapter_area_title">
          <span class="detail-tit__num yongsin">8</span>
          <span class="detail-tit__txt yongsin">용/희/기/구/한</span>
        </div>
        <div class="personality-wrapper with-tit content_info_area_box">
          <div class="person-content-box">
            <div class="ys-img-0601">
              <YongsinFiveChart
                :hanDefaultData="hanDefaultData"
                :hanjijangData="hanjijangData"
                :chartColor="hanChartColor"
              />
            </div>
            <div class="ys-0601-exp-box">
              <div v-for="(html, idx) in hanchartDesc" :key="idx">
                <span class="yongsin-exp__txt" v-html="html"></span>
              </div>
            </div>
            <div class="ys-exp-wrap">
              <div class="tit-box">용/희/기/구/한</div>
            </div>
            <div class="ys-img-0601">
              <YongsinDonutChart :fiveChardData="hanjijangData" :chartColor="hanChartColor" />
            </div>
            <div class="ys-0601-bottom" :class="{ mobile: isMobile }">
              <div class="txt-box" v-for="(element, key) in hanChartColor" :key="key">
                <div class="txt1" :class="fiveMap[element]?.class">
                  {{ fiveMap[element]?.hanja || '-' }}
                </div>
                <div class="txt2">{{ key }}</div>
              </div>
            </div>
          </div>
        </div>
      </VCard>
    </VCol>
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-second">
      <VCard class="personality-card full-height">
        <div class="detail-card__top-title yongsin content_chapter_area_title">
          <span class="detail-tit__num yongsin">9</span>
          <span class="detail-tit__txt yongsin">선정용신</span>
        </div>

        <div class="personality-wrapper content_info_area_box">
          <div class="ys-candidates-wrap">
            <div class="ys-candidates-box" :class="yongClass">
              <div class="title-box" :class="yongClass">{{ selectedType }} {{ yongLabel }}</div>
              <div class="item-box">
                <div class="row-box">
                  <div class="label">분석용신</div>
                  <div class="desc">
                    <span class="circle-txt" :class="yongClass">{{ yongLabel }}</span>
                  </div>
                </div>
                <div class="row-box">
                  <div class="label">용신확률</div>
                  <div class="desc st-red">{{ yongPosibility }}</div>
                </div>
                <div class="row-box">
                  <div class="label">신뢰 구간</div>
                  <div class="desc">{{ confidenceScore }}</div>
                </div>
                <div class="row-box">
                  <div class="label">분석신뢰도</div>
                  <div class="desc">{{ confideceLevel }}</div>
                </div>
                <div class="row-box">
                  <div class="label">격국패턴</div>
                  <div class="desc">{{ ggPattern }}</div>
                </div>
              </div>
            </div>
            <div class="ys-candidates-box st-gray">
              <div class="title-box">용신후보</div>
              <div class="item-box">
                <div class="row-box" v-for="(item, idx) in yongsinCandidates" :key="idx">
                  <div class="label">{{ item.label }}</div>
                  <div class="desc">
                    <template v-if="item.elements">
                      <div
                        v-for="(el, i) in item.elements"
                        :key="i"
                        class="circle-txt"
                        :class="el.class"
                      >
                        {{ el.hanja }}
                      </div>
                    </template>
                    <template v-else> - </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="yg-line-01 mt-none"></div>
          <div class="ys-step-wrap">
            <div class="item-box">
              <div class="tite-box">
                <div class="label">1.</div>
                <div class="desc">교차분석 결과 (규칙 기반 + 인공지능)</div>
              </div>
              <span class="text-box" v-html="formattedAnalysisSteps[0]" />
            </div>
            <div class="item-box">
              <div class="tite-box">
                <div class="label">2.</div>
                <div class="desc">용신 신뢰구간</div>
              </div>
              <div class="text-box" v-html="formattedAnalysisSteps[1]"></div>
            </div>
            <div class="item-box">
              <div class="tite-box">
                <div class="label">3.</div>
                <div class="desc">용신 활용방법</div>
              </div>
              <div class="text-box" v-html="formattedAnalysisSteps[2]"></div>
            </div>
          </div>
        </div>
      </VCard>
    </VCol>
  </VRow>
</template>
<style scoped lang="scss">
@import '@/assets/nangman_style/_util.scss';
@import '@/assets/nangman_style/youngsin.scss';
@import '@/assets/nangman_style/_analysis_main_color.scss';

.ys-step-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  .item-box {
    display: flex;
    padding: 24px 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    align-self: stretch;
    border-radius: 12px;
    background: var(--Gray-200, #f8f8f8);
    .tite-box {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      align-self: stretch;
      .label {
        color: $analysis-yongsin;
        font-family: Pretendard;
        font-size: 18px;
        font-style: normal;
        font-weight: 700;
        line-height: 28px; /* 155.556% */
      }
      .desc {
        color: var(--Gray-800, #333);
        font-family: Pretendard;
        font-size: 18px;
        font-style: normal;
        font-weight: 700;
        line-height: 28px; /* 155.556% */
      }
    }
    .text-box {
      color: var(--Gray-800, #333);
      font-family: Pretendard;
      font-size: 15px;
      font-style: normal;
      font-weight: 400;
      line-height: 24px; /* 160% */
      .highlight-text {
        display: inline-flex;
        padding: 0 4px;
        justify-content: center;
        align-items: center;
        gap: 10px;
        background: $analysis-yongsin;
        color: #fff;
        font-family: Pretendard;
        font-size: 20px;
        font-style: normal;
        font-weight: 700;
        line-height: 24px; /* 120% */
      }
    }
  }
}
.ys-candidates-wrap {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
}
.ys-candidates-box {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 0 0;
  border-radius: 12px;
  border: 1px solid var(--black-100, #404040);
  background: rgba(64, 64, 64, 0.05);
  max-width: 60%;
  &.five01 {
    border: 1px solid$fiveColor01;
  }
  &.five02 {
    border: 1px solid $fiveColor02;
  }
  &.five03 {
    border: 1px solid $fiveColor03;
  }
  &.five04 {
    border: 1px solid $fiveColor04;
  }
  &.five05 {
    border: 1px solid $fiveColor05;
  }
  &.st-gray {
    max-width: 40%;
    border-radius: 12px;
    border: 1px solid var(--Gray-300, #ebebeb);
    background: var(--gray-100-white, #fff);
    .title-box {
      background: var(--Gray-300, #ebebeb);
      color: var(--Gray-700, #575757);
    }
  }
  .title-box {
    display: flex;
    padding: 8px 12px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    align-self: stretch;
    // background: var(--black-100, #404040);
    color: var(--gray-100-white, #fff);
    font-family: Pretendard;
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: 28px; /* 155.556% */
    border-radius: 12px 12px 0 0;
    &.five01 {
      background: $fiveColor01;
    }
    &.five02 {
      background: $fiveColor02;
    }
    &.five03 {
      background: $fiveColor03;
    }
    &.five04 {
      background: $fiveColor04;
    }
    &.five05 {
      background: $fiveColor05;
    }
  }
  .item-box {
    display: flex;
    padding: 20px;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    align-self: stretch;
    .row-box {
      display: flex;
      justify-content: space-between;
      align-items: center;
      align-self: stretch;
      .label {
        color: var(--Gray-600, #757575);
        font-family: Pretendard;
        font-size: 13px;
        font-style: normal;
        font-weight: 700;
        line-height: 20px; /* 153.846% */
      }
      .desc {
        color: var(--Gray-800, #333);
        font-family: Pretendard;
        font-size: 16px;
        font-style: normal;
        font-weight: 700;
        line-height: 26px; /* 162.5% */
        display: flex;
        align-items: flex-start;
        gap: 4px;
      }
    }
  }
  .circle-txt {
    display: flex;
    width: 26px;
    height: 26px;
    padding: 3px;
    justify-content: center;
    align-items: center;
    aspect-ratio: 1/1;
    border-radius: 30px;
    color: var(--gray-100-white, #fff);
    text-align: center;
    font-family: 'ChosunGs';
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px; /* 125% */
    &.five01 {
      background: $fiveColor01;
    }
    &.five02 {
      background: $fiveColor02;
    }
    &.five03 {
      background: $fiveColor03;
    }
    &.five04 {
      background: $fiveColor04;
    }
    &.five05 {
      background: $fiveColor05;
    }
    &.none {
      color: var(--Gray-500, #949494);
      background: transparent;
      font-weight: 700;
    }
  }
}
.ys-img-0601 {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.ys-img-0602 {
  width: 100%;
  height: 180px;
  background-image: url('@manseImages/ys-img-0602.png');
  background-size: 100% auto;
  background-position: center;
}
.ys-0601-exp-box {
  padding: 16px;
  border-radius: 12px;
  border: 1px solid var(--Gray-300, #ebebeb);
  .yongsin-exp__txt {
    color: var(--Gray-800, #333);
    text-align: center;
    font-family: Pretendard;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
    .p-keyword {
      color: $analysis-yongsin !important;
      font-weight: 500;
      display: inline;
    }
  }
}
.ys-0601-bottom {
  display: flex;
  padding: 10px 0;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  border-radius: 12px;
  border: 1px solid var(--Gray-300, #ebebeb);
  background: var(--Gray-200, #f8f8f8);
  &.mobile {
    display: grid;
    grid-template-columns: repeat(3, max-content);
    justify-content: center;
    column-gap: 16px;
    row-gap: 10px;
    .txt-box {
      flex: initial;
    }
    .txt-box:nth-child(1) {
      grid-column: 1;
    }
    .txt-box:nth-child(2) {
      grid-column: 3;
    }
  }
  .txt-box {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    flex: 1 0 0;
    .txt1 {
      display: flex;
      width: 32px;
      height: 32px;
      padding: 4px;
      justify-content: center;
      align-items: center;
      aspect-ratio: 1/1;
      border-radius: 50px;
      font-family: 'ChosunGs';
      color: var(--gray-100-white, #fff);
      text-align: center;
      font-size: 20px;
      font-style: normal;
      font-weight: 400;
      line-height: 24px; /* 120% */
      &.five01 {
        background: $fiveColor01;
      }
      &.five02 {
        background: $fiveColor02;
      }
      &.five03 {
        background: $fiveColor03;
      }
      &.five04 {
        background: $fiveColor04;
      }
      &.five05 {
        background: $fiveColor05;
      }
    }
    .txt2 {
      color: var(--Gray-800, #333);
      text-align: center;
      font-family: Pretendard;
      font-size: 15px;
      font-style: normal;
      font-weight: 700;
      line-height: 24px; /* 160% */
    }
  }
}
@media (max-width: 768px) {
  .ys-candidates-wrap {
    flex-direction: column;
    width: 100%;
    .ys-candidates-box {
      max-width: 100%;
      width: 100%;
    }
  }
}
</style>
